import type { Tool } from './tool';

/**
 * A logical group of tools shown across the Tools catalog, Skills page,
 * and Agent detail page.
 */
export interface ToolGroup {
  /** Unique group key. */
  key: string;
  /** Human-readable group label. */
  label: string;
  /** Group description. */
  description: string;
  /** All tools belonging to this group, sorted for display. */
  tools: Tool[];
}

/** The `coding` parent tool — excluded because its sub-tools are shown directly. */
const EXCLUDED_TOOL_KEYS = new Set(['coding']);

/**
 * Derive two tool groups from a flat list of tools, based on handler:
 *
 * - **Coding Tools** — all tools whose config.handler is 'coding'
 *   (i.e. sub-tools that route to the coding-agent service).
 *   The parent `coding` tool is excluded.
 * - **Platform Tools** — everything else: delegation, vision, reporting,
 *   file I/O, todo, and any builtin tools that aren't coding sub-tools.
 *
 * Groups always appear in Coding → Platform order. Tools are sorted
 * alphabetically by name within each group.
 */
export function deriveToolGroups(tools: Tool[]): ToolGroup[] {
  const coding: Tool[] = [];
  const platform: Tool[] = [];

  for (const t of tools) {
    if (EXCLUDED_TOOL_KEYS.has(t.key)) continue;
    // Coding sub-tools have config.kind === 'builtin' with handler === 'coding'.
    // Everything else (delegate, vision, report, file, todo, HTTP, MCP, etc.) is Platform.
    if (t.config && 'handler' in t.config && t.config.handler === 'coding') {
      coding.push(t);
    } else {
      platform.push(t);
    }
  }

  return [
    buildGroup('coding', 'Coding Tools', 'Code editing, git, shell, tests, web search, browser automation, file system, and more.', coding),
    buildGroup('platform', 'Platform Tools', 'Agent delegation, vision analysis, task tracking, file I/O, and report generation.', platform),
  ].filter((g) => g.tools.length > 0);
}

function buildGroup(key: string, label: string, description: string, tools: Tool[]): ToolGroup {
  return {
    key,
    label,
    description,
    tools: [...tools].sort((a, b) => a.name.localeCompare(b.name)),
  };
}

